// noinspection GrazieInspection

import { html, View } from 'rune-ts';
import { ProductImageView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/ProductImageView.ts';
import { ProductTitleView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/ProductTitleView.ts';
import { PriceView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/PriceView.ts';
import klass from './CardProductView.module.scss';
import { classes } from '@services/MarppleShop/shared/util';
import {
  BadgeSubView,
  TBadgeSubWithoutSize,
} from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/BadgeSubView.ts';
import { ShopView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/ShopView.ts';
import { CountViewView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/CountViewView.ts';
import { ReviewView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/ReviewView.ts';
import { ProductThumbnail } from '@services/MarppleShop/features/ProductList/type';
import { TBadgeMainWithoutSize } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/BadgeMainView.ts';
import { RealtimeView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/RealtimeView.ts';
import { assert, assertIsDefined } from '@services/MarppleShop/shared/util/assert.ts';
import { differenceInHours, differenceInMinutes, isAfter } from 'date-fns';
import { drop, filter, head, isNil, pipe, sort, take, toArray } from '@fxts/core';
import { ELang } from '@services/MarppleShop/shared/enum/ELang.ts';
import { GOODS_TYPE, GoodsTypeId } from '@services/MarppleShop/features/Product/type';
import { ProductStockDto } from '@services/MarppleShop/features/product-stock/application/dto';
import { DetachedCountViewView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/DetachedCountViewView.ts';

export class CardProductError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = this.constructor.name;
  }
}

type TProductImage = {
  thumbnail_info: ProductThumbnail;
  is_liked: boolean;
  is_lazy: boolean;
  resize_width: number;
  domain_name: string;
  id: number;
  name: string;
  store_name: string;
};

type TProductTitle = {
  domain_name: string;
  id: number;
  name: string;
};

type TPrice = {
  price: number;
  price_en: number | null;
  price_jp: number | null;
};

type TShop = {
  store_name: string;
  domain_name: string;
  is_partner: boolean;
};

type TShopWithProfileImageUrl = {
  store_name: string;
  domain_name: string;
  profile_image_url: string | null;
  is_partner: boolean;
};

type TShopWithProfileImageUrlAndWatcherCount = {
  store_name: string;
  domain_name: string;
  profile_image_url: string | null;
  watcher_count: number | undefined;
  is_partner: boolean;
};

type TReview = {
  review_count: number;
  review_score: number;
};

type TCountView = {
  watcher_count: number | undefined;
};

type TRealtime = {
  ordered_at: string;
  currentTime: string;
};

type TBadgeMain = {
  ranking?: number;
  quantity: number | null;
  is_quantity_public: boolean;
  sell_end_at: string | null;
  currentTime: string;
  prev_total_sales_amount: string | null;
  current_total_sales_amount: string | null;
  realtime_current_rank: number | null;
  /**
   * @TODO sws: 캐시 만료 후 optional 삭제
   */
  product_shipping_policy?: {
    free_shipping_min_price: number | null;
    shipping_price: number | null;
  };
  price: number;
  goods_type_id: GoodsTypeId;
  stockInfo: ProductStockDto;
};

type TBadgeRealtime = {
  ranking?: number;
  ordered_at: string;
  currentTime: string;
};

type TBadgeSub = {
  sell_end_at: string | null;
  quantity: number | null;
  is_quantity_public: boolean;
  currentTime: string;
  prev_total_sales_amount: string | null;
  current_total_sales_amount: string | null;
  realtime_current_rank: number | null;
  /**
   * @TODO sws: 캐시 만료 후 optional 삭제
   */
  product_shipping_policy?: {
    free_shipping_min_price: number | null;
    shipping_price: number | null;
  };
  price: number;
  goods_type_id: GoodsTypeId;
  stockInfo: ProductStockDto;
};

/**
 * Type=A, State=Default, Size=Xsmall, Layout=Horizontal, Theme=Light
 * Type=A, State=Adult, Size=Xsmall, Layout=Horizontal, Theme=Light
 * Type=A, State=Adult+AgeVerification, Size=Xsmall, Layout=Horizontal, Theme=Light
 * Type=A, State=Soldout, Size=Xsmall, Layout=Horizontal, Theme=Light
 * Type=A, State=Soldout+Adult, Size=Xsmall, Layout=Horizontal, Theme=Light
 * Type=A, State=Soldout+Adult+AgeVerification, Size=Xsmall, Layout=Horizontal, Theme=Light
 *
 * Type=A, State=Default, Size=Xsmall, Layout=Horizontal, Theme=Dark
 * Type=A, State=Adult, Size=Xsmall, Layout=Horizontal, Theme=Dark
 * Type=A, State=Adult+AgeVerification, Size=Xsmall, Layout=Horizontal, Theme=Dark
 * Type=A, State=Soldout, Size=Xsmall, Layout=Horizontal, Theme=Dark
 * Type=A, State=Soldout+Adult, Size=Xsmall, Layout=Horizontal, Theme=Dark
 * Type=A, State=Soldout+Adult+AgeVerification, Size=Xsmall, Layout=Horizontal, Theme=Dark
 *
 * Type=A, State=Default, Size=Small, Layout=Horizontal, Theme=Light
 * Type=A, State=Adult, Size=Small, Layout=Horizontal, Theme=Light
 * Type=A, State=Adult+AgeVerification, Size=Small, Layout=Horizontal, Theme=Light
 * Type=A, State=Soldout, Size=Small, Layout=Horizontal, Theme=Light
 * Type=A, State=Soldout+Adult, Size=Small, Layout=Horizontal, Theme=Light
 * Type=A, State=Soldout+Adult+AgeVerification, Size=Small, Layout=Horizontal, Theme=Light
 *
 * Type=A, State=Default, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=A, State=Adult, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=A, State=Adult+AgeVerification, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout+Adult, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout+Adult+AgeVerification, Size=Xsmall, Layout=Vertical, Theme=Light
 *
 * Type=A, State=Default, Size=Medium, Layout=Vertical, Theme=Light
 * Type=A, State=Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=A, State=Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout, Size=Medium, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout+Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout+Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 *
 * Type=A, State=Default, Size=Large, Layout=Vertical, Theme=Light
 * Type=A, State=Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=A, State=Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout, Size=Large, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout+Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout+Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 */
type TTypeAProps =
  | {
      type: 'A';
      state: 'Default';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Default';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Default';
      size: 'Small';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult';
      size: 'Small';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult+AgeVerification';
      size: 'Small';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout';
      size: 'Small';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult';
      size: 'Small';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Small';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Default';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Default';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Default';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    };

/**
 * Type=B, State=Default, Size=Small, Layout=Vertical, Theme=Dark
 * Type=B, State=Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=B, State=Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 * Type=B, State=Soldout, Size=Small, Layout=Vertical, Theme=Dark
 * Type=B, State=Soldout+Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=B, State=Soldout+Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 *
 * Type=B, State=Default, Size=Medium, Layout=Vertical, Theme=Light
 * Type=B, State=Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=B, State=Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 * Type=B, State=Soldout, Size=Medium, Layout=Vertical, Theme=Light
 * Type=B, State=Soldout+Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=B, State=Soldout+Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 *
 * Type=B, State=Default, Size=Large, Layout=Vertical, Theme=Light
 * Type=B, State=Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=B, State=Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 * Type=B, State=Soldout, Size=Large, Layout=Vertical, Theme=Light
 * Type=B, State=Soldout+Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=B, State=Soldout+Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 *
 * Type=B, State=Default, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=B, State=Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=B, State=Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=B, State=Soldout, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=B, State=Soldout+Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=B, State=Soldout+Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 */
type TTypeBProps =
  | {
      type: 'B';
      state: 'Default';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_realtime: TBadgeRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_realtime: TBadgeRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_realtime: TBadgeRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_realtime: TBadgeRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_realtime: TBadgeRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_realtime: TBadgeRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Default';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Default';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Default';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      badge_realtime: TBadgeRealtime;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      badge_realtime: TBadgeRealtime;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      badge_realtime: TBadgeRealtime;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      badge_realtime: TBadgeRealtime;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      badge_realtime: TBadgeRealtime;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      badge_realtime: TBadgeRealtime;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    };

/**
 * Type=C, State=Default, Size=Medium, Layout=Vertical, Theme=Light
 * Type=C, State=Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=C, State=Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 * Type=C, State=Soldout, Size=Medium, Layout=Vertical, Theme=Light
 * Type=C, State=Soldout+Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=C, State=Soldout+Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 *
 * Type=C, State=Default, Size=Large, Layout=Vertical, Theme=Light
 * Type=C, State=Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=C, State=Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 * Type=C, State=Soldout, Size=Large, Layout=Vertical, Theme=Light
 * Type=C, State=Soldout+Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=C, State=Soldout+Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 */
type TTypeCProps =
  | {
      type: 'C';
      state: 'Default';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Soldout';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Soldout+Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Default';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Soldout';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Soldout+Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    };

/**
 * Type=D, State=Default, Size=Medium, Layout=Vertical, Theme=Light
 * Type=D, State=Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=D, State=Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 * Type=D, State=Soldout, Size=Medium, Layout=Vertical, Theme=Light
 * Type=D, State=Soldout+Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=D, State=Soldout+Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 *
 * Type=D, State=Default, Size=Large, Layout=Vertical, Theme=Light
 * Type=D, State=Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=D, State=Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 * Type=D, State=Soldout, Size=Large, Layout=Vertical, Theme=Light
 * Type=D, State=Soldout+Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=D, State=Soldout+Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 */
type TTypeDProps =
  | {
      state: 'Default';
      type: 'D';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Adult';
      type: 'D';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Adult+AgeVerification';
      type: 'D';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Soldout';
      type: 'D';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Soldout+Adult';
      type: 'D';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Soldout+Adult+AgeVerification';
      type: 'D';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Default';
      type: 'D';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Adult';
      type: 'D';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Adult+AgeVerification';
      type: 'D';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Soldout';
      type: 'D';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Soldout+Adult';
      type: 'D';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Soldout+Adult+AgeVerification';
      type: 'D';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    };

/**
 * Type=E, State=Default, Size=Xsmall, Layout=Vertical, Theme=Dark
 * Type=E, State=Adult, Size=Xsmall, Layout=Vertical, Theme=Dark
 * Type=E, State=Adult+AgeVerification, Size=Xsmall, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout, Size=Xsmall, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout+Adult, Size=Xsmall, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout+Adult+AgeVerification, Size=Xsmall, Layout=Vertical, Theme=Dark
 *
 * Type=E, State=Default, Size=Small, Layout=Vertical, Theme=Dark
 * Type=E, State=Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=E, State=Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout, Size=Small, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout+Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout+Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 *
 * Type=E, State=Default, Size=Medium, Layout=Vertical, Theme=Light
 * Type=E, State=Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=E, State=Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 * Type=E, State=Soldout, Size=Medium, Layout=Vertical, Theme=Light
 * Type=E, State=Soldout+Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=E, State=Soldout+Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 *
 * Type=E, State=Default, Size=Large, Layout=Vertical, Theme=Light
 * Type=E, State=Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=E, State=Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 * Type=E, State=Soldout, Size=Large, Layout=Vertical, Theme=Light
 * Type=E, State=Soldout+Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=E, State=Soldout+Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 *
 * Type=E, State=Default, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=E, State=Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=E, State=Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout+Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout+Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 */
type TTypeEProps =
  | {
      type: 'E';
      state: 'Default';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Default';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Default';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Default';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Default';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    };

/**
 * Type=F, State=Default, Size=Small, Layout=Vertical, Theme=Dark
 * Type=F, State=Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=F, State=Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 * Type=F, State=Soldout, Size=Small, Layout=Vertical, Theme=Dark
 * Type=F, State=Soldout+Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=F, State=Soldout+Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 *
 * Type=F, State=Default, Size=Medium, Layout=Vertical, Theme=Light
 * Type=F, State=Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=F, State=Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 * Type=F, State=Soldout, Size=Medium, Layout=Vertical, Theme=Light
 * Type=F, State=Soldout+Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=F, State=Soldout+Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 *
 * Type=F, State=Default, Size=Large, Layout=Vertical, Theme=Light
 * Type=F, State=Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=F, State=Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 * Type=F, State=Soldout, Size=Large, Layout=Vertical, Theme=Light
 * Type=F, State=Soldout+Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=F, State=Soldout+Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 *
 * Type=F, State=Default, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=F, State=Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=F, State=Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=F, State=Soldout, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=F, State=Soldout+Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=F, State=Soldout+Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 */
type TTypeFProps =
  | {
      type: 'F';
      state: 'Default';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Default';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Default';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Default';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    };

/**
 * Type=G, State=Default, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=G, State=Adult, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=G, State=Adult+AgeVerification, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout+Adult, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout+Adult+AgeVerification, Size=Xsmall, Layout=Vertical, Theme=Light
 *
 * Type=G, State=Default, Size=Small, Layout=Vertical, Theme=Dark
 * Type=G, State=Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=G, State=Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 * Type=G, State=Soldout, Size=Small, Layout=Vertical, Theme=Dark
 * Type=G, State=Soldout+Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=G, State=Soldout+Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 *
 * Type=G, State=Default, Size=Medium, Layout=Vertical, Theme=Light
 * Type=G, State=Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=G, State=Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout, Size=Medium, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout+Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout+Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 *
 * Type=G, State=Default, Size=Large, Layout=Vertical, Theme=Light
 * Type=G, State=Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=G, State=Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout, Size=Large, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout+Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout+Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 *
 * Type=G, State=Default, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=G, State=Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=G, State=Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=G, State=Soldout, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=G, State=Soldout+Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=G, State=Soldout+Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 */
type TTypeGProps =
  | {
      type: 'G';
      state: 'Default';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Default';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Default';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Default';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Default';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_sub: TBadgeSub;
      onProductImageLikeClick: () => void;
    };

export type TProps =
  | TTypeAProps
  | TTypeBProps
  | TTypeCProps
  | TTypeDProps
  | TTypeEProps
  | TTypeFProps
  | TTypeGProps;

/**
 * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=26351-76410&m=dev
 */
export class CardProductView extends View<TProps> {
  static getState({
    stockInfo,
    currentTime,
    sell_end_at,
    adultMeta,
    is_user_adult_verified,
  }: {
    sell_end_at: string | null;
    currentTime: string;
    stockInfo: ProductStockDto;
    adultMeta: { isAdult: boolean } | undefined;
    is_user_adult_verified: boolean;
  }): TProps['state'] {
    const isSoldOut =
      (sell_end_at && isAfter(new Date(currentTime), new Date(sell_end_at))) || stockInfo.isOutOfStock;
    const isAdult = !!adultMeta;
    const isAgeVerified = is_user_adult_verified;

    if (isSoldOut && isAdult && isAgeVerified) {
      return 'Soldout+Adult+AgeVerification';
    } else if (isSoldOut && isAdult && !isAgeVerified) {
      return 'Soldout+Adult';
    } else if (isSoldOut && !isAdult) {
      return 'Soldout';
    } else if (!isSoldOut && isAdult && isAgeVerified) {
      return 'Adult+AgeVerification';
    } else if (!isSoldOut && isAdult && !isAgeVerified) {
      return 'Adult';
    } else {
      return 'Default';
    }
  }

  setProductImageIsLiked(is_liked: boolean) {
    this.data.product_image.is_liked = is_liked;
    const productImageView = this.subView(ProductImageView);
    assertIsDefined(productImageView, new CardProductError());
    productImageView.setIsLiked(is_liked);
  }

  setBadgeRealtimeCurrentTime(currentTime: string) {
    const { type, size } = this.data;

    assert(type === 'B' && (size === 'Small' || size === 'Xlarge'));

    this.data.badge_realtime.currentTime = currentTime;
    const productImageView = this.subView(ProductImageView);
    assertIsDefined(productImageView, new CardProductError());
    productImageView.setBadgeRealtimeWithoutSize({
      ...this.data.badge_realtime,
      currentTime,
    });
  }

  setRealtimeCurrentTime(currentTime: string) {
    const { type, size } = this.data;

    assert(type === 'B' && (size === 'Medium' || size === 'Large'));

    this.data.realtime.currentTime = currentTime;
    const realtimeView = this.subView(RealtimeView);
    assertIsDefined(realtimeView, new CardProductError());
    realtimeView.setCurrentTime(currentTime);
  }

  private getProductImageState() {
    return this.data.state === 'Soldout' ||
      this.data.state === 'Soldout+Adult' ||
      this.data.state === 'Soldout+Adult+AgeVerification'
      ? 'Soldout'
      : 'Default';
  }

  private getProductTitleAdult() {
    return this.data.state === 'Adult' ||
      this.data.state === 'Adult+AgeVerification' ||
      this.data.state === 'Soldout+Adult' ||
      this.data.state === 'Soldout+Adult+AgeVerification'
      ? 'On'
      : 'Off';
  }

  private getBadgeMainWithoutSize(): TBadgeMainWithoutSize | undefined {
    const { type } = this.data;

    assert(
      type === 'C' || type === 'D' || type === 'E' || type === 'F' || type === 'G',
      new CardProductError(),
    );

    const {
      ranking,
      currentTime,
      sell_end_at,
      quantity,
      is_quantity_public,
      current_total_sales_amount,
      prev_total_sales_amount,
      realtime_current_rank,
      product_shipping_policy,
      price,
      goods_type_id,
      stockInfo,
    } = this.data.badge_main;

    const isSoldOut =
      this.data.state === 'Soldout' ||
      this.data.state === 'Soldout+Adult' ||
      this.data.state === 'Soldout+Adult+AgeVerification';

    let displayAvailableStock;
    if (stockInfo.availableStock && stockInfo.displayConfig.enabled) {
      if (stockInfo.displayConfig.underThreshold) {
        displayAvailableStock =
          stockInfo.availableStock < stockInfo.displayConfig.underThreshold ? stockInfo.availableStock : null;
      } else {
        displayAvailableStock = stockInfo.availableStock;
      }
    } else {
      displayAvailableStock = null;
    }

    /**
     * https://www.notion.so/marpple/21853281635280b7b19dc0d13ca7994b?source=copy_link#24c53281635280f8910efa6afa8de2d8
     */
    const badgeMainCandidates = [
      {
        type: 'Time_left',
        ranking,
        sell_end_at,
        currentTime,
        score:
          isNil(sell_end_at) ||
          isAfter(new Date(currentTime), new Date(sell_end_at)) ||
          differenceInHours(new Date(sell_end_at), new Date(currentTime)) > 24 ||
          isSoldOut
            ? 0
            : 1 + 0.05 * (1440 - differenceInMinutes(new Date(sell_end_at), new Date(currentTime))),
      },
      {
        type: 'Days_left',
        ranking,
        sell_end_at,
        currentTime,
        score:
          isNil(sell_end_at) ||
          isAfter(new Date(currentTime), new Date(sell_end_at)) ||
          differenceInHours(new Date(sell_end_at), new Date(currentTime)) <= 24 ||
          isSoldOut
            ? 0
            : 1 + 0.05 * (1440 - differenceInMinutes(new Date(sell_end_at), new Date(currentTime))),
      },
      {
        type: 'Stock_left',
        ranking,
        displayAvailableStock,
        // https://www.notion.so/21853281635280b7b19dc0d13ca7994b?d=288532816352803ebe18001cb8762410&source=copy_link#24c5328163528098b9e4cef9327c4824
        score:
          isNil(displayAvailableStock) || displayAvailableStock === 0 || isSoldOut
            ? 0
            : 1 - (displayAvailableStock - 10) ** 3,
      },
      {
        type: 'Limited',
        ranking,
        quantity,
        score:
          goods_type_id !== GOODS_TYPE.MPS || !is_quantity_public || isNil(quantity) || isSoldOut
            ? 0
            : 6 - 0.05 * quantity,
      },
      {
        type: 'Free_Shipping',
        ranking,
        score:
          ET.lang === ELang.Kr &&
          (goods_type_id === GOODS_TYPE.PBG || goods_type_id === GOODS_TYPE.TPL) &&
          (product_shipping_policy?.shipping_price === 0 ||
            (product_shipping_policy?.free_shipping_min_price &&
              product_shipping_policy.free_shipping_min_price <= price))
            ? 1
            : 0,
      },
      {
        type: 'Trend',
        ranking,
        score:
          isNil(prev_total_sales_amount) ||
          isNil(current_total_sales_amount) ||
          parseFloat(current_total_sales_amount) < 0 ||
          parseFloat(prev_total_sales_amount) <= 0
            ? 0
            : (parseFloat(current_total_sales_amount) / parseFloat(prev_total_sales_amount) - 1) ** 2,
      },
      {
        type: 'Popular',
        ranking,
        score: isNil(realtime_current_rank) ? 0 : 2 - ((10 * (realtime_current_rank - 1)) / 100) ** 2,
      },
    ].filter(({ score }) => score >= 1) as (TBadgeMainWithoutSize & {
      score: number;
    })[];

    if (badgeMainCandidates.length === 0 && ranking) {
      return {
        type: 'Rank',
        ranking,
      };
    }

    return pipe(
      badgeMainCandidates,
      filter(({ score }) => score >= 1),
      sort((a, b) => b.score - a.score),
      head,
    );
  }

  private getBadgeSubsWithoutSize(): TBadgeSubWithoutSize[] {
    const { type } = this.data;

    assert(type === 'C' || type === 'D' || type === 'F' || type === 'G', new CardProductError());

    const {
      currentTime,
      sell_end_at,
      quantity,
      is_quantity_public,
      current_total_sales_amount,
      prev_total_sales_amount,
      realtime_current_rank,
      product_shipping_policy,
      price,
      goods_type_id,
      stockInfo,
    } = this.data.badge_sub;

    const isSoldOut =
      this.data.state === 'Soldout' ||
      this.data.state === 'Soldout+Adult' ||
      this.data.state === 'Soldout+Adult+AgeVerification';

    let displayAvailableStock;
    if (stockInfo.availableStock && stockInfo.displayConfig.enabled) {
      if (stockInfo.displayConfig.underThreshold) {
        displayAvailableStock =
          stockInfo.availableStock < stockInfo.displayConfig.underThreshold ? stockInfo.availableStock : null;
      } else {
        displayAvailableStock = stockInfo.availableStock;
      }
    } else {
      displayAvailableStock = null;
    }

    /**
     * https://www.notion.so/marpple/21853281635280b7b19dc0d13ca7994b?source=copy_link#24c53281635280f8910efa6afa8de2d8
     */
    const badgeSubCandidates = [
      {
        type: 'Time_left',
        sell_end_at,
        currentTime,
        score:
          isNil(sell_end_at) || isAfter(new Date(currentTime), new Date(sell_end_at)) || isSoldOut
            ? 0
            : 1 + 0.05 * (1440 - differenceInMinutes(new Date(sell_end_at), new Date(currentTime))),
      },
      {
        type: 'Stock_left',
        displayAvailableStock,
        score:
          isNil(displayAvailableStock) || displayAvailableStock === 0 || isSoldOut
            ? 0
            : 1 - (displayAvailableStock - 10) ** 3,
      },
      {
        type: 'Limited',
        quantity,
        score:
          goods_type_id !== GOODS_TYPE.MPS || !is_quantity_public || isNil(quantity) || isSoldOut
            ? 0
            : 6 - 0.05 * quantity,
      },
      {
        type: 'Free_Shipping',
        score:
          ET.lang === ELang.Kr &&
          (goods_type_id === GOODS_TYPE.PBG || goods_type_id === GOODS_TYPE.TPL) &&
          (product_shipping_policy?.shipping_price === 0 ||
            (product_shipping_policy?.free_shipping_min_price &&
              product_shipping_policy.free_shipping_min_price <= price))
            ? 1
            : 0,
      },
      {
        type: 'Trend',
        score:
          isNil(prev_total_sales_amount) ||
          isNil(current_total_sales_amount) ||
          parseFloat(current_total_sales_amount) < 0 ||
          parseFloat(prev_total_sales_amount) <= 0
            ? 0
            : (parseFloat(current_total_sales_amount) / parseFloat(prev_total_sales_amount) - 1) ** 2,
      },
      {
        type: 'Popular',
        score: isNil(realtime_current_rank) ? 0 : 2 - ((10 * (realtime_current_rank - 1)) / 100) ** 2,
      },
    ].filter(({ score }) => score >= 1) as (TBadgeSubWithoutSize & {
      score: number;
    })[];

    return pipe(
      badgeSubCandidates,
      filter(({ score }) => score >= 1),
      sort((a, b) => b.score - a.score),
      drop(1),
      take(3),
      toArray,
    );
  }

  protected override template(props: TProps) {
    if (props.type === 'A' && props.size === 'Xsmall' && props.layout === 'Horizontal') {
      const { theme, product_image, product_title, price, state, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_a_size_xsmall_layout_horizontal}">
        <div class="${klass.product_image_wrapper}">
          ${new ProductImageView({
            ...product_image,
            state: this.getProductImageState(),
            size: 'Small',
            onLikeClick: onProductImageLikeClick,
          })}
        </div>

        <div
          class="${classes(klass.product_info, {
            [klass.theme_dark]: theme === 'Dark',
            [klass.state_soldout]:
              state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
          })}"
        >
          ${new ProductTitleView({
            ...product_title,
            size: '13px',
            adult: this.getProductTitleAdult(),
          })}
          ${new PriceView({ ...price, size: '13px' })}
        </div>
      </div>`;
    } else if (props.type === 'A' && props.size === 'Small' && props.layout === 'Horizontal') {
      const { product_image, product_title, price, state, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_a_size_small_layout_horizontal}">
        <div class="${klass.product_image_wrapper}">
          ${new ProductImageView({
            ...product_image,
            state: this.getProductImageState(),
            size: 'Small',
            onLikeClick: onProductImageLikeClick,
          })}
        </div>

        <div
          class="${classes(klass.product_info, {
            [klass.state_soldout]:
              state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
          })}"
        >
          ${new ProductTitleView({ ...product_title, size: '14px', adult: this.getProductTitleAdult() })}
          ${new PriceView({ ...price, size: '14px' })}
        </div>
      </div>`;
    } else if (props.type === 'A' && props.size === 'Xsmall' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_a_size_xsmall_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Small',
          onLikeClick: onProductImageLikeClick,
        })}

        <div
          class="${classes(klass.product_info, {
            [klass.state_soldout]:
              state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
          })}"
        >
          ${new ProductTitleView({ ...product_title, size: '12px', adult: this.getProductTitleAdult() })}
          ${new PriceView({ ...price, size: '12px' })}
        </div>
      </div>`;
    } else if (props.type === 'A' && props.size === 'Medium' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_a_size_medium_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          onLikeClick: onProductImageLikeClick,
        })}

        <div
          class="${classes(klass.product_info, {
            [klass.state_soldout]:
              state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
          })}"
        >
          ${new ProductTitleView({ ...product_title, size: '13px', adult: this.getProductTitleAdult() })}
          ${new PriceView({ ...price, size: '14px' })}
        </div>
      </div>`;
    } else if (props.type === 'A' && props.size === 'Large' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_a_size_large_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          onLikeClick: onProductImageLikeClick,
        })}

        <div
          class="${classes(klass.product_info, {
            [klass.state_soldout]:
              state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
          })}"
        >
          ${new ProductTitleView({ ...product_title, size: '15px', adult: this.getProductTitleAdult() })}
          ${new PriceView({ ...price, size: '16px' })}
        </div>
      </div>`;
    } else if (props.type === 'B' && props.size === 'Small' && props.layout === 'Vertical') {
      const {
        product_image,
        shop,
        product_title,
        price,
        count_view,
        badge_realtime,
        onProductImageLikeClick,
      } = props;

      const watcherCount = count_view.watcher_count;

      return html`<div class="${klass.type_b_size_small_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_realtime_without_size: badge_realtime,
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Box', theme: 'Dark', size: '11px' })}

          <div class="${klass.product_info_inner}">
            <div class="${klass.product_info_inner_inner}">
              ${new ProductTitleView({ ...product_title, size: '13px', adult: this.getProductTitleAdult() })}
              ${new PriceView({ ...price, size: '14px' })}
            </div>

            ${watcherCount
              ? new DetachedCountViewView({
                  ...count_view,
                  watcher_count: watcherCount,
                  size: 'Small',
                })
              : null}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'B' && props.size === 'Medium' && props.layout === 'Vertical') {
      const {
        state,
        product_image,
        shop,
        product_title,
        price,
        realtime,
        count_view,
        onProductImageLikeClick,
      } = props;

      const watcherCount = count_view.watcher_count;

      return html`<div class="${klass.type_b_size_medium_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Line', theme: 'Light', size: '11px' })}

          <div class="${klass.product_info_inner}">
            <div
              class="${classes(klass.product_info_inner_inner, {
                [klass.state_soldout]:
                  state === 'Soldout' ||
                  state === 'Soldout+Adult' ||
                  state === 'Soldout+Adult+AgeVerification',
              })}"
            >
              ${new ProductTitleView({ ...product_title, size: '13px', adult: this.getProductTitleAdult() })}
              ${new PriceView({ ...price, size: '14px' })}
            </div>

            ${new RealtimeView({
              ...realtime,
              size: 'Small',
            })}
            ${watcherCount
              ? new CountViewView({
                  ...count_view,
                  watcher_count: watcherCount,
                  size: 'Small',
                })
              : null}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'B' && props.size === 'Large' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, realtime, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_b_size_large_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Line', theme: 'Light', size: '13px' })}

          <div class="${klass.product_info_inner}">
            <div
              class="${classes(klass.product_info_inner_inner, {
                [klass.state_soldout]:
                  state === 'Soldout' ||
                  state === 'Soldout+Adult' ||
                  state === 'Soldout+Adult+AgeVerification',
              })}"
            >
              ${new ProductTitleView({ ...product_title, size: '15px', adult: this.getProductTitleAdult() })}
              ${new PriceView({ ...price, size: '16px' })}
            </div>

            ${new RealtimeView({
              ...realtime,
              size: 'Medium',
            })}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'B' && props.size === 'Xlarge' && props.layout === 'Vertical') {
      const { product_image, shop, product_title, price, badge_realtime, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_b_size_xlarge_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_realtime_without_size: badge_realtime,
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Box', theme: 'Dark', size: '13px' })}

          <div class="${klass.product_info_inner}">
            ${new ProductTitleView({ ...product_title, size: '16px', adult: this.getProductTitleAdult() })}
            ${new PriceView({ ...price, size: '18px' })}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'C' && props.size === 'Medium' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_c_size_medium_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info_detail}">
          <div
            class="${classes(klass.info, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ProductTitleView({ ...product_title, size: '13px', adult: this.getProductTitleAdult() })}
            ${new PriceView({ ...price, size: '14px' })}
          </div>

          <div class="${klass.badge_sub}">
            ${badge_subs_without_size.map(
              (badge_sub_without_size) =>
                new BadgeSubView({ ...badge_sub_without_size, size: 'Medium (Medium~Small)' }),
            )}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'C' && props.size === 'Large' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_c_size_large_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info_detail}">
          <div
            class="${classes(klass.info, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ProductTitleView({ ...product_title, size: '15px', adult: this.getProductTitleAdult() })}
            ${new PriceView({ ...price, size: '16px' })}
          </div>

          <div class="${klass.badge_sub}">
            ${badge_subs_without_size.map(
              (badge_sub_without_size) =>
                new BadgeSubView({ ...badge_sub_without_size, size: 'Large (Xlarge~Large)' }),
            )}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'D' && props.size === 'Medium' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, count_view, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      const watcherCount = count_view.watcher_count;

      return html`<div class="${klass.type_d_size_medium_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info}">
          <div
            class="${classes(klass.product_info_inner, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ShopView({ type: 'Text', theme: 'Light', size: '12px', ...shop })}

            <div class="${klass.product_info_detail}">
              ${new ProductTitleView({ ...product_title, size: '13px', adult: this.getProductTitleAdult() })}
              ${new PriceView({ ...price, size: '14px' })}
            </div>
          </div>

          <div class="${klass.badge_sub}">
            ${badge_subs_without_size.map(
              (badge_sub_without_size) =>
                new BadgeSubView({ ...badge_sub_without_size, size: 'Medium (Medium~Small)' }),
            )}
          </div>

          ${watcherCount
            ? new CountViewView({ ...count_view, watcher_count: watcherCount, size: 'Small' })
            : null}
        </div>
      </div>`;
    } else if (props.type === 'D' && props.size === 'Large' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, count_view, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      const watcherCount = count_view.watcher_count;

      return html`<div class="${klass.type_d_size_large_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info}">
          <div
            class="${classes(klass.product_info_inner, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ShopView({ ...shop, type: 'Text', theme: 'Light', size: '13px' })}

            <div class="${klass.product_info_detail}">
              ${new ProductTitleView({ ...product_title, size: '15px', adult: this.getProductTitleAdult() })}
              ${new PriceView({ ...price, size: '16px' })}
            </div>
          </div>

          <div class="${klass.badge_sub}">
            ${badge_subs_without_size.map(
              (badge_sub_without_size) =>
                new BadgeSubView({ ...badge_sub_without_size, size: 'Large (Xlarge~Large)' }),
            )}
          </div>

          ${watcherCount
            ? new CountViewView({ ...count_view, watcher_count: watcherCount, size: 'Medium' })
            : null}
        </div>
      </div>`;
    } else if (props.type === 'E' && props.size === 'Xsmall' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_e_size_xsmall_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xsmall',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div
          class="${classes(klass.product_info, {
            [klass.state_soldout]:
              state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
          })}"
        >
          ${new ShopView({ ...shop, type: 'Text', theme: 'Light', size: '11px' })}

          <div class="${klass.product_info_inner}">
            ${new ProductTitleView({ ...product_title, size: '12px', adult: this.getProductTitleAdult() })}
            ${new PriceView({ ...price, size: '12px' })}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'E' && props.size === 'Small' && props.layout === 'Vertical') {
      const { product_image, shop, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_e_size_small_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Box', theme: 'Dark', size: '11px' })}

          <div class="${klass.product_info_inner}">
            ${new ProductTitleView({ ...product_title, size: '13px', adult: this.getProductTitleAdult() })}
            ${new PriceView({ ...price, size: '14px' })}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'E' && props.size === 'Medium' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_e_size_medium_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div
          class="${classes(klass.product_info, {
            [klass.state_soldout]:
              state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
          })}"
        >
          ${new ShopView({ ...shop, type: 'Text', theme: 'Light', size: '12px' })}

          <div class="${klass.product_info_inner}">
            ${new ProductTitleView({ ...product_title, size: '13px', adult: this.getProductTitleAdult() })}
            ${new PriceView({ ...price, size: '14px' })}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'E' && props.size === 'Large' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_e_size_large_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div
          class="${classes(klass.product_info, {
            [klass.state_soldout]:
              state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
          })}"
        >
          ${new ShopView({ ...shop, type: 'Text', theme: 'Light', size: '13px' })}

          <div class="${klass.product_info_inner}">
            ${new ProductTitleView({ ...product_title, size: '15px', adult: this.getProductTitleAdult() })}
            ${new PriceView({ ...price, size: '16px' })}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'E' && props.size === 'Xlarge' && props.layout === 'Vertical') {
      const { product_image, shop, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_e_size_xlarge_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${classes(klass.product_info)}">
          ${new ShopView({ ...shop, type: 'Box', theme: 'Dark', size: '13px' })}

          <div class="${klass.product_info_inner}">
            ${new ProductTitleView({ ...product_title, size: '16px', adult: this.getProductTitleAdult() })}
            ${new PriceView({ ...price, size: '18px' })}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'F' && props.size === 'Small' && props.layout === 'Vertical') {
      const { product_image, shop, product_title, price, review, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_f_size_small_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.info_wrapper}">
          ${new ShopView({ ...shop, type: 'Box', theme: 'Dark', size: '11px' })}

          <div class="${klass.product_info_wrapper}">
            <div class="${klass.product_info}">
              ${new ProductTitleView({ ...product_title, size: '13px', adult: this.getProductTitleAdult() })}
              ${new PriceView({ ...price, size: '15px' })}
            </div>

            <div class="${klass.badge_sub}">
              ${badge_subs_without_size.map(
                (badge_sub_without_size) =>
                  new BadgeSubView({ ...badge_sub_without_size, size: 'Medium (Medium~Small)' }),
              )}
            </div>

            ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Dark', size: 'Small' }) : null}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'F' && props.size === 'Medium' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, review, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_f_size_medium_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Line', theme: 'Light', size: '11px' })}

          <div class="${klass.product_info_inner}">
            <div
              class="${classes(klass.product_info_inner_inner, {
                [klass.state_soldout]:
                  state === 'Soldout' ||
                  state === 'Soldout+Adult' ||
                  state === 'Soldout+Adult+AgeVerification',
              })}"
            >
              ${new ProductTitleView({ ...product_title, size: '13px', adult: this.getProductTitleAdult() })}
              ${new PriceView({ ...price, size: '14px' })}
            </div>

            <div class="${klass.badge_sub}">
              ${badge_subs_without_size.map(
                (badge_sub_without_size) =>
                  new BadgeSubView({ ...badge_sub_without_size, size: 'Medium (Medium~Small)' }),
              )}
            </div>

            ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Light', size: 'Small' }) : null}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'F' && props.size === 'Large' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, review, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_f_size_large_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Line', theme: 'Light', size: '13px' })}

          <div class="${klass.product_info_inner}">
            <div
              class="${classes(klass.product_info_inner_inner, {
                [klass.state_soldout]:
                  state === 'Soldout' ||
                  state === 'Soldout+Adult' ||
                  state === 'Soldout+Adult+AgeVerification',
              })}"
            >
              ${new ProductTitleView({ ...product_title, size: '15px', adult: this.getProductTitleAdult() })}
              ${new PriceView({ ...price, size: '16px' })}
            </div>

            <div class="${klass.badge_sub}">
              ${badge_subs_without_size.map(
                (badge_sub_without_size) =>
                  new BadgeSubView({ ...badge_sub_without_size, size: 'Large (Xlarge~Large)' }),
              )}
            </div>

            ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Light', size: 'Medium' }) : null}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'F' && props.size === 'Xlarge' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, review, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_f_size_xlarge_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Box', theme: 'Dark', size: '13px' })}

          <div class="${klass.product_info_inner}">
            <div
              class="${classes(klass.product_info_inner_inner, {
                [klass.state_soldout]:
                  state === 'Soldout' ||
                  state === 'Soldout+Adult' ||
                  state === 'Soldout+Adult+AgeVerification',
              })}"
            >
              ${new ProductTitleView({ ...product_title, size: '16px', adult: this.getProductTitleAdult() })}
              ${new PriceView({ ...price, size: '20px' })}
            </div>

            <div class="${klass.badge_sub}">
              ${badge_subs_without_size.map(
                (badge_sub_without_size) =>
                  new BadgeSubView({ ...badge_sub_without_size, size: 'Large (Xlarge~Large)' }),
              )}
            </div>

            ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Dark', size: 'Medium' }) : null}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'G' && props.size === 'Xsmall' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, review, count_view, onProductImageLikeClick } =
        props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      const watcherCount = count_view.watcher_count;

      return html`<div class="${klass.type_g_size_xsmall_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xsmall',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info}">
          <div
            class="${classes(klass.product_info_inner, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ProductTitleView({ ...product_title, size: '12px', adult: this.getProductTitleAdult() })}
            ${new PriceView({ ...price, size: '12px' })}
          </div>

          <div class="${klass.badge_sub}">
            ${badge_subs_without_size.map(
              (badge_sub_without_size) =>
                new BadgeSubView({ ...badge_sub_without_size, size: 'Medium (Medium~Small)' }),
            )}
          </div>

          ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Light', size: 'Small' }) : null}
          ${watcherCount
            ? new CountViewView({ ...count_view, watcher_count: watcherCount, size: 'Small' })
            : null}
        </div>
      </div>`;
    } else if (props.type === 'G' && props.size === 'Small' && props.layout === 'Vertical') {
      const { product_image, product_title, price, review, count_view, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      const watcherCount = count_view.watcher_count;

      return html`<div class="${klass.type_g_size_small_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info}">
          <div class="${klass.product_info_inner}">
            ${new ProductTitleView({ ...product_title, size: '13px', adult: this.getProductTitleAdult() })}
            ${new PriceView({ ...price, size: '14px' })}
          </div>

          <div class="${klass.badge_sub}">
            ${badge_subs_without_size.map(
              (badge_sub_without_size) =>
                new BadgeSubView({ ...badge_sub_without_size, size: 'Medium (Medium~Small)' }),
            )}
          </div>

          ${review.review_count ? new ReviewView({ ...review, theme: 'Dark', size: 'Small' }) : null}
          ${watcherCount
            ? new CountViewView({ ...count_view, watcher_count: watcherCount, size: 'Small' })
            : null}
        </div>
      </div>`;
    } else if (props.type === 'G' && props.size === 'Medium' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, review, count_view, onProductImageLikeClick } =
        props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      const watcherCount = count_view.watcher_count;

      return html`<div class="${klass.type_g_size_medium_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info}">
          <div
            class="${classes(klass.product_info_inner, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ProductTitleView({ ...product_title, size: '13px', adult: this.getProductTitleAdult() })}
            ${new PriceView({ ...price, size: '14px' })}
          </div>

          <div class="${klass.badge_sub}">
            ${badge_subs_without_size.map(
              (badge_sub_without_size) =>
                new BadgeSubView({ ...badge_sub_without_size, size: 'Medium (Medium~Small)' }),
            )}
          </div>

          ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Light', size: 'Small' }) : null}
          ${watcherCount
            ? new CountViewView({ ...count_view, watcher_count: watcherCount, size: 'Small' })
            : null}
        </div>
      </div>`;
    } else if (props.type === 'G' && props.size === 'Large' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, review, count_view, onProductImageLikeClick } =
        props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      const watcherCount = count_view.watcher_count;

      return html`<div class="${klass.type_g_size_large_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info}">
          <div
            class="${classes(klass.product_info_inner, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ProductTitleView({ ...product_title, size: '15px', adult: this.getProductTitleAdult() })}
            ${new PriceView({ ...price, size: '16px' })}
          </div>

          <div class="${klass.sub_wrapper}">
            <div class="${klass.badge_sub}">
              ${badge_subs_without_size.map(
                (badge_sub_without_size) =>
                  new BadgeSubView({ ...badge_sub_without_size, size: 'Large (Xlarge~Large)' }),
              )}
            </div>

            ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Light', size: 'Medium' }) : null}
            ${watcherCount
              ? new CountViewView({ ...count_view, watcher_count: watcherCount, size: 'Medium' })
              : null}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'G' && props.size === 'Xlarge' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, review, count_view, onProductImageLikeClick } =
        props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      const watcherCount = count_view.watcher_count;

      return html`<div class="${klass.type_g_size_xlarge_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: onProductImageLikeClick,
        })}

        <div class="${klass.product_info}">
          <div
            class="${classes(klass.product_info_inner, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ProductTitleView({ ...product_title, size: '16px', adult: this.getProductTitleAdult() })}
            ${new PriceView({ ...price, size: '18px' })}
          </div>

          <div class="${klass.badge_sub}">
            ${badge_subs_without_size.map(
              (badge_sub_without_size) =>
                new BadgeSubView({ ...badge_sub_without_size, size: 'Large (Xlarge~Large)' }),
            )}
          </div>

          ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Dark', size: 'Medium' }) : null}
          ${watcherCount
            ? new CountViewView({ ...count_view, watcher_count: watcherCount, size: 'Medium' })
            : null}
        </div>
      </div>`;
    } else {
      throw new CardProductError();
    }
  }
}
